/// <amd-module name="Repos/Views/Settings/Repositories-Policies/Components/PolicyCard" />
import * as React from "react";
import * as Resources from "Repos/Views/Settings/Repositories-Policies/Resources";
import { useObservable } from "VSS/Core/Observable";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Button } from "VSSUI/Button";
import { CardContent, CustomCard } from "VSSUI/Card";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow } from "VSSUI/Header";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Pill } from "VSSUI/Pill";
import { css } from "VSSUI/Util";

export interface IPolicyCardProps {
    className: string;
    filter: React.ReactNode;
    headerCommandBarItems?: IHeaderCommandBarItem[];
    headerDescriptionText: string;
    itemCount: number;
    policyName: string;
    titleText: string;
}

export const PolicyCard = (props: React.PropsWithChildren<IPolicyCardProps>): JSX.Element => {
    const { className, children, filter, headerCommandBarItems, headerDescriptionText, itemCount, policyName, titleText } = props;

    const initiallyCollapsed = tryGetLocalStorageValue(`repos.policysettings.${policyName}.collapsed`) === "true";
    const [collapsed, setCollapsed] = useObservable(initiallyCollapsed);

    return (
        <Observer collapsed={collapsed}>
            {({ collapsed }: { collapsed: boolean }) => (
                <CustomCard
                    className={css(
                        "repos-policy-card flex-noshrink bolt-card-with-header",
                        (itemCount > 0 || collapsed) && "no-bottom-padding",
                        className
                    )}
                >
                    <div className="flex-row">
                        <Button
                            ariaLabel={collapsed ? Resources.PolicyExpandText : Resources.PolicyCollapseText}
                            subtle={true}
                            iconProps={{
                                iconName: collapsed ? "ChevronRightMed" : "ChevronDownMed",
                                size: IconSize.small
                            }}
                            className="bolt-card-expand-button flex-self-start"
                            onClick={() => {
                                setCollapsed(!collapsed);
                                trySetLocalStorageValue(`repos.policysettings.${policyName}.collapsed`, collapsed ? undefined : "true");
                            }}
                        />
                        <CustomHeader className="flex-grow bolt-card-header repos-policy-header scroll-hidden">
                            <HeaderTitleArea>
                                <HeaderTitleRow>
                                    <HeaderTitle>{titleText}</HeaderTitle>
                                    <Pill className="margin-left-8" excludeFocusZone={true} excludeTabStop={true}>
                                        {itemCount}
                                    </Pill>
                                </HeaderTitleRow>
                                <HeaderDescription>{headerDescriptionText}</HeaderDescription>
                            </HeaderTitleArea>
                            {filter}
                            {headerCommandBarItems && <HeaderCommandBar items={headerCommandBarItems} />}
                        </CustomHeader>
                    </div>
                    {!collapsed && (
                        <CardContent className={css(collapsed && "hidden")} contentPadding={false}>
                            {children}
                        </CardContent>
                    )}
                </CustomCard>
            )}
        </Observer>
    );
};
